import React from 'react';
import { Card } from '../../components/ui/Card';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { BackButton } from '../../components/ui/BackButton';
import { Clock, Shield, Wifi, AlertTriangle, Award, Info, Smartphone } from 'lucide-react';

export const GeneralInstructionsPage: React.FC = () => {
    const sections = [
        {
            title: 'Registration and Arrival',
            icon: <Clock className="w-6 h-6 text-[#7C4DFF]" />,
            items: [
                'All teams are required to reach the host school by 8:00 AM for registration.',
                'Participating schools must send no more than 25 participants to the event.',
                'Each participating school will be allotted a Team Code which should be used by the participants for all purposes of identification.'
            ]
        },
        {
            title: 'Participant Requirements',
            icon: <Shield className="w-6 h-6 text-[#FF991C]" />,
            items: [
                'Participants must carry a valid school ID card for verification purposes.',
                'Participants are expected to maintain decorum and discipline throughout the event.',
                'Participants must report to their respective event venues at least 15 minutes before the scheduled start time.'
            ]
        },
        {
            title: 'Device and Equipment Guidelines',
            icon: <Smartphone className="w-6 h-6 text-[#EF4444]" />,
            items: [
                'For events requiring personal devices (Framesmith, Pixel Forge, Polytope, Arena Protocol), participants must bring their own laptops/tablets/gaming devices.',
                'Participants must ensure their devices are fully charged. Limited charging points may be available.',
                'For events where devices are provided (Brandcraft), participants are not allowed to use personal devices.',
                'Participants are responsible for the safety and security of their personal devices and belongings.'
            ]
        },
        {
            title: 'Internet Connectivity',
            icon: <Wifi className="w-6 h-6 text-amber-400" />,
            items: [
                'For events requiring internet access, participants should arrange their own mobile hotspot or internet connectivity.',
                'The host school will provide WiFi access where applicable, but connectivity is not guaranteed.'
            ]
        },
        {
            title: 'Conduct and Fair Play',
            icon: <AlertTriangle className="w-6 h-6 text-[#EF4444]" />,
            items: [
                'Usage of unfair means by any participant will result in immediate disqualification.',
                'Participants must not indulge in any form of malpractice, cheating, or plagiarism.',
                'Participants must respect fellow competitors, judges, and organizing team members.',
                'Any form of inappropriate behavior, derogatory language, or unsportsmanlike conduct will lead to disqualification.'
            ]
        },
        {
            title: 'Judging and Decisions',
            icon: <Award className="w-6 h-6 text-[#FF991C]" />,
            items: [
                'The decision of the judges shall be final and binding for all events.',
                'No arguments or disputes regarding judging will be entertained.',
                'Results will be announced at the closing ceremony.'
            ]
        }
    ];

    return (
        <PublicLayout>
            <div className="min-h-screen bg-[#0A0A0A] pt-24 pb-12 px-4 md:px-8">
                <div className="max-w-5xl mx-auto space-y-12">
                    {/* Header */}
                    <div className="text-center space-y-4 relative">
                        <div className="absolute left-0 top-0 hidden md:block">
                            <BackButton />
                        </div>
                        <div className="md:hidden flex justify-start mb-4">
                            <BackButton />
                        </div>
                        <h1 className="text-4xl md:text-6xl font-display font-bold text-white tracking-tight">
                            General <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF991C] to-[#7C4DFF]">Instructions</span>
                        </h1>
                        <p className="text-lg md:text-xl text-white/60 max-w-2xl mx-auto">
                            Important guidelines for all participating schools and students.
                        </p>
                    </div>

                    {/* Instructions Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {sections.map((section, index) => (
                            <Card key={index} className="p-6 bg-white/[0.03] border border-white/[0.08] rounded-2xl hover:border-[#FF991C]/20 transition-colors">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                                        {section.icon}
                                    </div>
                                    <h3 className="text-xl font-bold text-white">{section.title}</h3>
                                </div>
                                <ul className="space-y-3">
                                    {section.items.map((item, idx) => (
                                        <li key={idx} className="flex items-start gap-3 text-white/60 text-sm leading-relaxed">
                                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#FF991C]/30 shrink-0" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </Card>
                        ))}
                    </div>

                    {/* Additional Info */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <Card className="p-6 bg-gradient-to-br from-[#FF991C]/10 to-transparent border border-[#FF991C]/20 rounded-2xl">
                            <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                                <Award className="w-5 h-5 text-[#FF991C]" /> Scoring System
                            </h3>
                            <ul className="space-y-2 text-sm text-white/60">
                                <li className="flex justify-between"><span>Winners</span> <span className="text-white font-bold">10 Pts</span></li>
                                <li className="flex justify-between"><span>1st Runners-up</span> <span className="text-white font-bold">8 Pts</span></li>
                                <li className="flex justify-between"><span>2nd Runners-up</span> <span className="text-white font-bold">6 Pts</span></li>
                                <li className="flex justify-between"><span>Participation</span> <span className="text-white font-bold">1 Pt</span></li>
                            </ul>
                        </Card>

                        <Card className="p-6 bg-white/[0.03] border border-white/[0.08] rounded-2xl">
                            <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                                <Info className="w-5 h-5 text-white/40" /> Miscellaneous
                            </h3>
                            <ul className="space-y-2 text-sm text-white/60">
                                <li>• Certificates for all participants</li>
                                <li>• Refreshments provided</li>
                                <li>• COVID-19 protocols apply</li>
                            </ul>
                        </Card>

                        <Card className="p-6 bg-gradient-to-br from-[#EF4444]/10 to-transparent border border-[#EF4444]/20 rounded-2xl">
                            <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5 text-[#EF4444]" /> Emergency
                            </h3>
                            <p className="text-sm text-white/60">
                                In case of any emergency, immediately contact event coordinators or volunteers at the venue.
                            </p>
                        </Card>
                    </div>
                </div>
            </div>
        </PublicLayout>
    );
};
